import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { ModalOverlay } from "../ModalOverlay";
import {
  emptyLocalLabForm,
  formatRemovedDate,
  REMOVAL_REASON_LABELS,
  type DetailModalMode,
  type DirectoryLab,
  type LocalLabForm,
  type Partner,
  type RemovalReason,
} from "./labPartnerTypes";

type LabPartnerDetailModalProps = {
  open: boolean;
  mode: DetailModalMode;
  partner?: Partner | null;
  directoryLab?: DirectoryLab | null;
  saving?: boolean;
  onClose: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
  onRestore?: () => void;
  onAdd?: () => void;
  onSavePersonal?: (form: LocalLabForm) => void;
  onCancelEdit?: () => void;
};

function Field({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-slate-800 dark:text-slate-100">
        {value?.trim() ? value.trim() : "—"}
      </dd>
    </div>
  );
}

export default function LabPartnerDetailModal({
  open,
  mode,
  partner = null,
  directoryLab = null,
  saving = false,
  onClose,
  onEdit,
  onRemove,
  onRestore,
  onAdd,
  onSavePersonal,
  onCancelEdit,
}: LabPartnerDetailModalProps) {
  const [form, setForm] = useState<LocalLabForm>(emptyLocalLabForm());

  useEffect(() => {
    if (!open) return;
    if (mode === "edit-personal" && partner) {
      setForm({
        labName: partner.name || "",
        contactName: partner.contactName || "",
        phone: partner.phone || "",
        email: partner.email || "",
        address: partner.address || "",
      });
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && !saving) onClose();
    }
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, mode, partner, onClose, saving]);

  if (!open) return null;

  const isDirectory = mode === "view-directory";
  const isEdit = mode === "edit-personal";
  const title = isDirectory
    ? directoryLab?.name || "Lab"
    : partner?.name || "Partner lab";
  const isPersonal = !!partner?.isShadow;
  const isRemoved =
    partner?.status === "removed" || partner?.status === "pending";

  return (
    <ModalOverlay
      className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lab-partner-detail-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        className="relative my-4 flex w-full max-w-lg flex-col rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800 sm:my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-700 sm:px-5">
          <div className="min-w-0 flex-1">
            <h2
              id="lab-partner-detail-title"
              className="text-base font-semibold text-slate-900 dark:text-slate-100"
            >
              {isEdit ? "Edit personal lab" : title}
            </h2>
            {!isDirectory && partner && (
              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={
                    isPersonal
                      ? "inline-flex rounded-full bg-slate-100 dark:bg-slate-700 px-2.5 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300"
                      : "inline-flex rounded-full bg-emerald-50 dark:bg-emerald-950/50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300"
                  }
                >
                  {isPersonal ? "Added locally" : "Registered lab"}
                </span>
                {isRemoved && (
                  <span className="inline-flex rounded-full bg-amber-50 dark:bg-amber-950/40 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200">
                    Removed
                  </span>
                )}
              </div>
            )}
            {isDirectory && (
              <span className="mt-2 inline-flex rounded-full bg-emerald-50 dark:bg-emerald-950/50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                Registered lab
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex size-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          {isEdit ? (
            <form
              id="edit-personal-lab-form"
              className="space-y-3 text-sm"
              onSubmit={(e) => {
                e.preventDefault();
                if (!form.labName.trim()) return;
                onSavePersonal?.(form);
              }}
            >
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                  Lab name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.labName}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, labName: e.target.value }))
                  }
                  className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                    Contact name
                  </label>
                  <input
                    type="text"
                    value={form.contactName}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        contactName: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                    Phone
                  </label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, phone: e.target.value }))
                    }
                    className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                  Address
                </label>
                <textarea
                  rows={2}
                  value={form.address}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, address: e.target.value }))
                  }
                  className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
                />
              </div>
            </form>
          ) : isDirectory && directoryLab ? (
            <dl className="grid gap-4 sm:grid-cols-2">
              <Field label="Lab name" value={directoryLab.name} />
              <Field label="Official name" value={directoryLab.officialName} />
              <Field label="Email" value={directoryLab.email} />
              <Field label="Phone" value={directoryLab.phone} />
              <div className="sm:col-span-2">
                <Field label="Address" value={directoryLab.address} />
              </div>
            </dl>
          ) : partner ? (
            <dl className="grid gap-4 sm:grid-cols-2">
              <Field label="Lab name" value={partner.name} />
              {!isPersonal && (
                <Field label="Official name" value={partner.officialName} />
              )}
              {isPersonal && (
                <Field label="Contact name" value={partner.contactName} />
              )}
              <Field label="Email" value={partner.email} />
              <Field label="Phone" value={partner.phone} />
              <div className="sm:col-span-2">
                <Field label="Address" value={partner.address} />
              </div>
              {!isPersonal && partner.status && !isRemoved && (
                <Field label="Partnership status" value={partner.status} />
              )}
              {isPersonal && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-slate-500">
                    Only visible to your clinic
                  </p>
                </div>
              )}
              {isRemoved && (
                <>
                  <Field
                    label="Removed on"
                    value={formatRemovedDate(partner.removedAt)}
                  />
                  <Field
                    label="Removal reason"
                    value={
                      partner.removalReason
                        ? REMOVAL_REASON_LABELS[
                            partner.removalReason as RemovalReason
                          ] || partner.removalReason
                        : undefined
                    }
                  />
                  {partner.removalNote?.trim() && (
                    <div className="sm:col-span-2">
                      <Field label="Removal note" value={partner.removalNote} />
                    </div>
                  )}
                </>
              )}
            </dl>
          ) : null}
        </div>

        <footer className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-700 sm:px-5">
          {isEdit ? (
            <>
              <button
                type="button"
                onClick={onCancelEdit}
                disabled={saving}
                className="rounded-lg border dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="edit-personal-lab-form"
                disabled={saving}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : isDirectory ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300"
              >
                Close
              </button>
              <button
                type="button"
                onClick={onAdd}
                disabled={saving}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {saving ? "Adding…" : "Add"}
              </button>
            </>
          ) : isRemoved ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300"
              >
                Close
              </button>
              <button
                type="button"
                onClick={onRestore}
                disabled={saving}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {saving ? "Restoring…" : "Restore"}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300"
              >
                Close
              </button>
              {isPersonal && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="rounded-lg border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Edit
                </button>
              )}
              <button
                type="button"
                onClick={onRemove}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Remove
              </button>
            </>
          )}
        </footer>
      </div>
    </ModalOverlay>
  );
}
